/**
 * TranscriptEditor — Coach-facing transcript management for video scenes.
 *
 * Provides:
 *   - "Generate Transcript" button that calls the transcription endpoint
 *   - Loading spinner during generation
 *   - Editable transcript segments after generation
 *   - Save corrections button
 *   - Delete transcript button
 *
 * Props:
 *   sceneId   — The scene to manage transcripts for
 *   segments  — Existing transcript segments (if any)
 *   onUpdate  — Callback when transcript is generated/saved/deleted
 */
import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_SCENARIO_OS_API || '/scenario-os';

function getToken() {
  return localStorage.getItem('scenario_os_token');
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function TranscriptEditor({ sceneId, segments: initialSegments, onUpdate }) {
  const [segments, setSegments] = useState(initialSegments || []);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setSegments(initialSegments || []);
    setDirty(false);
  }, [initialSegments]);

  const clearMessages = () => { setError(''); setSuccess(''); };

  const handleGenerate = useCallback(async () => {
    clearMessages();
    setGenerating(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/coach/transcripts/${encodeURIComponent(sceneId)}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSegments(data.segments || []);
      setSuccess(`Transcript generated: ${data.segment_count} segments`);
      setDirty(false);
      if (onUpdate) onUpdate(data.segments);
    } catch (e) {
      setError(e.message || 'Failed to generate transcript');
    } finally {
      setGenerating(false);
    }
  }, [sceneId, onUpdate]);

  const handleSave = useCallback(async () => {
    clearMessages();
    setSaving(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/coach/transcripts/${encodeURIComponent(sceneId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ segments }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setSuccess('Transcript saved successfully');
      setDirty(false);
      if (onUpdate) onUpdate(segments);
    } catch (e) {
      setError(e.message || 'Failed to save transcript');
    } finally {
      setSaving(false);
    }
  }, [sceneId, segments, onUpdate]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Delete transcript? This cannot be undone.')) return;
    clearMessages();
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/coach/transcripts/${encodeURIComponent(sceneId)}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok && res.status !== 204) {
        throw new Error(`HTTP ${res.status}`);
      }
      setSegments([]);
      setSuccess('Transcript deleted');
      setDirty(false);
      if (onUpdate) onUpdate([]);
    } catch (e) {
      setError(e.message || 'Failed to delete transcript');
    }
  }, [sceneId, onUpdate]);

  const updateSegmentText = (index, newText) => {
    setSegments((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], text: newText };
      return updated;
    });
    setDirty(true);
    clearMessages();
  };

  const hasSegments = segments.length > 0;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect x="1" y="4" width="16" height="2" rx="1" fill="#3b82f6" />
            <rect x="1" y="8" width="12" height="2" rx="1" fill="#3b82f6" />
            <rect x="1" y="12" width="14" height="2" rx="1" fill="#3b82f6" />
          </svg>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Transcripts</span>
        </div>
        {hasSegments && (
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {segments.length} segment{segments.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {error && (
        <div style={errorBoxStyle}>{error}</div>
      )}
      {success && (
        <div style={successBoxStyle}>{success}</div>
      )}

      {!hasSegments && !generating && (
        <div style={emptyStateStyle}>
          <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.6 }}>CC</div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, lineHeight: 1.5 }}>
            No transcript yet. Generate one from the video's audio using AI transcription.
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={generateBtnStyle}
          >
            Generate Transcript
          </button>
        </div>
      )}

      {generating && (
        <div style={loadingStyle}>
          <style>{`@keyframes transcript-spin { to { transform: rotate(360deg); } }`}</style>
          <div style={spinnerStyle} />
          <span style={{ fontSize: 13, color: '#475569' }}>Transcribing audio...</span>
        </div>
      )}

      {hasSegments && !generating && (
        <>
          <div style={segmentListStyle}>
            {segments.map((seg, i) => (
              <div key={i} style={segmentRowStyle}>
                <div style={segmentTimestampStyle}>
                  {formatTime(seg.start_sec)} – {formatTime(seg.end_sec)}
                </div>
                <textarea
                  value={seg.text}
                  onChange={(e) => updateSegmentText(i, e.target.value)}
                  rows={2}
                  style={segmentTextareaStyle}
                />
              </div>
            ))}
          </div>

          <div style={actionRowStyle}>
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              style={{
                ...saveBtnStyle,
                opacity: dirty ? 1 : 0.5,
                cursor: dirty ? 'pointer' : 'default',
              }}
            >
              {saving ? 'Saving...' : 'Save Corrections'}
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              style={regenerateBtnStyle}
            >
              Regenerate
            </button>
            <button
              onClick={handleDelete}
              style={deleteBtnStyle}
            >
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const containerStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  overflow: 'hidden',
  background: '#fff',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  marginTop: 16,
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
};

const errorBoxStyle = {
  margin: '8px 12px',
  padding: '8px 12px',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 6,
  fontSize: 13,
  color: '#991b1b',
};

const successBoxStyle = {
  margin: '8px 12px',
  padding: '8px 12px',
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  borderRadius: 6,
  fontSize: 13,
  color: '#166534',
};

const emptyStateStyle = {
  padding: '24px 20px',
  textAlign: 'center',
};

const generateBtnStyle = {
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 7,
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const loadingStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  padding: '28px 20px',
};

const spinnerStyle = {
  width: 20,
  height: 20,
  border: '2px solid #e2e8f0',
  borderTopColor: '#3b82f6',
  borderRadius: '50%',
  animation: 'transcript-spin 0.8s linear infinite',
};

const segmentListStyle = {
  maxHeight: 360,
  overflowY: 'auto',
  padding: '8px 12px',
};

const segmentRowStyle = {
  marginBottom: 10,
  padding: '8px 10px',
  background: '#f8fafc',
  borderRadius: 6,
  border: '1px solid #f1f5f9',
};

const segmentTimestampStyle = {
  fontSize: 11,
  color: '#64748b',
  fontFamily: 'monospace',
  marginBottom: 4,
};

const segmentTextareaStyle = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #e2e8f0',
  borderRadius: 4,
  fontSize: 13,
  lineHeight: 1.5,
  resize: 'vertical',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const actionRowStyle = {
  display: 'flex',
  gap: 8,
  padding: '10px 12px',
  borderTop: '1px solid #e2e8f0',
  background: '#fafbfc',
};

const saveBtnStyle = {
  background: '#059669',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '7px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const regenerateBtnStyle = {
  background: '#fff',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '7px 14px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const deleteBtnStyle = {
  background: '#fff',
  color: '#dc2626',
  border: '1px solid #fecaca',
  borderRadius: 6,
  padding: '7px 14px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  marginLeft: 'auto',
};
